import { genericMemo } from "@redotech/react-util/component";
import { useInput } from "@redotech/react-util/form";
import { LoadState, useTriggerLoad } from "@redotech/react-util/load";
import { Input, input } from "@redotech/ui/form";
import { type Validator } from "@redotech/ui/validator";
import { instantToEpochSeconds } from "@redotech/util/temporal";
import { JSXElementConstructor, useEffect, useState } from "react";
import { ButtonSize } from "../button";
import { TextSizeValue, TextWeightValue } from "../theme/typography";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "../utils/local-storage-wrapper";

const OTP_INFO_KEY = "redo.otp_info";
export const RESEND_CODE_DELAY = 30;

/**
 * Responsible for all login business logic. Renders the LoginComponent for the
 * component's presentation.
 */
export const RedoLoginContainer = genericMemo(function RedoLoginContainer<T>({
  LoginComponent,
  loginProps: {
    otpParams,
    onLogin,
    teamId,
    showMarketingCheckbox,
    subscribeToMarketing,
    setAuth,
    submitButtonParams,
    promptParams,
    textParams,
  },
  loginComponentProps,
}: {
  LoginComponent: JSXElementConstructor<RedoLoginComponentProps<T>>;
  loginProps: RedoLoginProps;
  loginComponentProps: T;
}) {
  const otpInfo: OtpInfo | null = getLocalStorageWithExpiry(OTP_INFO_KEY, {
    returnNullIfMissingExpiry: true,
  });
  const [emailEntered, setEmailEntered] = useState<string>(
    otpInfo?.email || "",
  );
  const [fourDigitCodeId, setFourDigitCodeId] = useState<string>(
    otpInfo?.fourDigitCodeId || "",
  );
  const [secondsSinceLastSent, setSecondsSinceLastSent] = useState<
    number | undefined
  >(
    otpInfo
      ? instantToEpochSeconds(Temporal.Now.instant()) - otpInfo.lastSentAt
      : undefined,
  );
  const [showIncorrectCodeError, setShowIncorrectCodeError] = useState(false);

  const requiredValidator: Validator<string> = (value) =>
    value ? [] : ["Email required"];
  const emailInput = useInput(
    input<string>({ validator: requiredValidator }),
    "",
  );
  const fourDigitCodeInput = useInput(input<string[]>(), ["", "", "", ""]);

  const [marketingAccepted, setMarketingAccepted] = useState<boolean>(true);

  const [emailSubmit, doEmailSubmit] = useTriggerLoad(async () => {
    if (!otpParams.submitOneTimePasscodeEmail) {
      return;
    }

    const email = (emailInput.value || emailEntered).toLowerCase();
    const response = await otpParams.submitOneTimePasscodeEmail({ email });
    setEmailEntered(email);
    setFourDigitCodeId(response.fourDigitCodeId);

    setLocalStorageWithExpiry(
      OTP_INFO_KEY,
      {
        email,
        fourDigitCodeId: response.fourDigitCodeId,
        lastSentAt: instantToEpochSeconds(Temporal.Now.instant()),
      },
      0.25,
    );
  });

  const [fourDigitCodeSubmit, doFourDigitCodeSubmit] = useTriggerLoad(
    async () => {
      if (
        !otpParams.verifyOneTimePasscode ||
        !otpParams.submitOneTimePasscodeEmail ||
        !teamId
      ) {
        return;
      }

      const response = await otpParams.verifyOneTimePasscode({
        fourDigitCodeId,
        passcode: fourDigitCodeInput.value.join(""),
        email: emailEntered,
        teamId,
        currentLocation: location.href,
      });

      const token = response.customerAuthToken;

      if (response.success && token) {
        setAuth?.(token);
        localStorage.removeItem(OTP_INFO_KEY);

        if (response.multipassUrl) {
          await fetch(response.multipassUrl, {
            credentials: "include",
            redirect: "manual",
          });
        }

        await onLogin?.(token);

        if (showMarketingCheckbox && marketingAccepted) {
          subscribeToMarketing(emailEntered, subscribeToMarketingText);
        }

        // Custom code to refresh the page for Gobi heat to reflect logged in state
        if (
          teamId === "66ce323fad2db2f9d76d5e1b" &&
          location.pathname.includes("militaryprogram")
        ) {
          location.reload();
        }
      } else {
        const submitResponse = await otpParams.submitOneTimePasscodeEmail({
          email: emailEntered,
        });
        setFourDigitCodeId(submitResponse.fourDigitCodeId);

        setLocalStorageWithExpiry(
          OTP_INFO_KEY,
          {
            email: emailEntered,
            fourDigitCodeId: submitResponse.fourDigitCodeId,
            lastSentAt: instantToEpochSeconds(Temporal.Now.instant()),
          },
          0.25,
        );

        fourDigitCodeInput.setValue(["", "", "", ""]);
        setShowIncorrectCodeError(true);
      }
    },
  );

  useEffect(() => {
    const otpInfo: OtpInfo | null = getLocalStorageWithExpiry(OTP_INFO_KEY, {
      returnNullIfMissingExpiry: true,
    });
    if (otpInfo) {
      setSecondsSinceLastSent(
        instantToEpochSeconds(Temporal.Now.instant()) - otpInfo.lastSentAt,
      );
    }

    const interval = setInterval(() => {
      const otpInfo: OtpInfo | null = getLocalStorageWithExpiry(OTP_INFO_KEY, {
        returnNullIfMissingExpiry: true,
      });
      if (!otpInfo) {
        setSecondsSinceLastSent(undefined);
        return;
      }

      setSecondsSinceLastSent(
        instantToEpochSeconds(Temporal.Now.instant()) - otpInfo.lastSentAt,
      );
    }, 1000);
    return () => clearInterval(interval);
  }, [fourDigitCodeId]);

  const handleEmailSubmit = async (e: any) => {
    e.preventDefault();
    doEmailSubmit();
  };

  const handleFourDigitCodeSubmit = async (e: any) => {
    e.preventDefault();
    doFourDigitCodeSubmit();
  };

  const handleChangeEmail = () => {
    localStorage.removeItem(OTP_INFO_KEY);
    setEmailEntered("");
    setFourDigitCodeId("");
    setShowIncorrectCodeError(false);
    emailInput.setValue("");
  };

  const subscribeToMarketingText = "Email me with news and offers";

  return (
    <LoginComponent
      doFourDigitCodeSubmit={doFourDigitCodeSubmit}
      emailEntered={emailEntered}
      emailInput={emailInput}
      emailSubmit={emailSubmit}
      fourDigitCodeId={fourDigitCodeId}
      fourDigitCodeInput={fourDigitCodeInput}
      fourDigitCodeSubmit={fourDigitCodeSubmit}
      handleChangeEmail={handleChangeEmail}
      handleEmailSubmit={handleEmailSubmit}
      handleFourDigitCodeSubmit={handleFourDigitCodeSubmit}
      loginComponentProps={loginComponentProps}
      marketingAccepted={marketingAccepted}
      promptParams={promptParams}
      secondsSinceLastSent={secondsSinceLastSent}
      setMarketingAccepted={setMarketingAccepted}
      showIncorrectCodeError={showIncorrectCodeError}
      showMarketingCheckbox={showMarketingCheckbox}
      submitButtonParams={submitButtonParams}
      subscribeToMarketingText={subscribeToMarketingText}
      textParams={textParams}
    />
  );
});

interface OtpInfo {
  email: string;
  fourDigitCodeId: string;
  lastSentAt: number;
}

export interface SubmitOneTimePasscodeEmailInputParams {
  email: string;
}

interface SubmitOneTimePasscodeEmailOutput {
  fourDigitCodeId: string;
}

export interface VerifyOneTimePasscodeInputParams {
  fourDigitCodeId: string;
  passcode: string;
  email: string;
  teamId: string;
  currentLocation?: string;
}

interface VerifyOneTimePasscodeOutput {
  success: boolean;
  customerAuthToken: string | undefined;
  multipassUrl: string | undefined;
}

export interface RedoLoginProps {
  onLogin?: (authToken: string) => Promise<void>;
  otpParams: {
    submitOneTimePasscodeEmail:
      | ((
          params: SubmitOneTimePasscodeEmailInputParams,
        ) => Promise<SubmitOneTimePasscodeEmailOutput>)
      | undefined;
    verifyOneTimePasscode:
      | ((
          params: VerifyOneTimePasscodeInputParams,
        ) => Promise<VerifyOneTimePasscodeOutput>)
      | undefined;
  };
  promptParams?: { promptText: string; promptSubtext?: string } | undefined;
  setAuth: ((authToken: string | undefined) => void) | undefined;
  submitButtonParams?:
    | {
        size?: ButtonSize | undefined;
        sendOneTimePasscodeButtonText?: string | undefined;
      }
    | undefined;
  teamId: string | undefined;
  textParams?: {
    fontSize?: TextSizeValue | undefined;
    fontWeight?: TextWeightValue | undefined;
  };
  showMarketingCheckbox: boolean;
  subscribeToMarketing: (email: string, agreementText: string) => void;
}

export interface RedoLoginComponentProps<T> {
  emailEntered: string;
  promptParams: RedoLoginProps["promptParams"];
  fourDigitCodeId: string;
  textParams: RedoLoginProps["textParams"];
  handleFourDigitCodeSubmit: (e: any) => Promise<void>;
  fourDigitCodeInput: Input<string[]>;
  fourDigitCodeSubmit: LoadState<void | undefined>;
  doFourDigitCodeSubmit: (signal?: AbortSignal) => void;
  submitButtonParams: RedoLoginProps["submitButtonParams"];
  secondsSinceLastSent: number | undefined;
  emailSubmit: LoadState<void | undefined>;
  handleEmailSubmit: (e: any) => Promise<void>;
  handleChangeEmail: () => void;
  showIncorrectCodeError: boolean;
  showMarketingCheckbox: boolean;
  emailInput: Input<string>;
  marketingAccepted: boolean;
  setMarketingAccepted: (value: boolean) => void;
  subscribeToMarketingText: string;
  loginComponentProps: T;
}
